import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFieldArray, useForm } from 'react-hook-form';

import useToggle from '@/data/hook/useToggle';
import { Section } from '@/models/Book';
import { ModularCard } from '@/components/ui/custom/modular/ModularCard';
import { TeacherBookColorIcon } from '@/components/icons';
import { updateSection } from '@/data/services/sectionServices';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import Instruction from './Instruction';
import SectionsPopup from './SectionsPopup';
import { twJoin } from 'tailwind-merge';
import { scrollBarYClassName } from '@/utils/scrollBarClassName';
import { useMutation } from '@tanstack/react-query';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import Subchapter from '@/models/Subchapter';
import Chapter from '@/models/Chapter';

type Props = {
  bookId: number;
  lessonId: number;
  subchapter: Subchapter;
  chapter: Chapter;
  sections?: Section[];
  readOnly?: boolean;
  onSubmit: (chapter: Chapter) => void;
};

export default function Instructions(props: Props) {
  const [t] = useTranslation('translation', {
    keyPrefix: 'common',
  });

  const {
    sections = [],
    subchapter,
    chapter,
    onSubmit,
    lessonId,
    bookId,
    readOnly,
  } = props;

  const subchapterId = subchapter.id;
  const chapterId = subchapter.chapterId;

  const { isOpen, toggle, open } = useToggle();

  const sectionsWithoutInstructions = sections.filter(
    section => section.instructions === null,
  );

  const sectionsWithInstructions = sections.filter(
    section => section.instructions !== null,
  );

  const { control, reset } = useForm<{ sections: Section[] }>({
    defaultValues: {
      sections: sectionsWithInstructions,
    },
  });

  useEffect(() => {
    reset({ sections });
  }, [sections, reset]);

  const { fields, update } = useFieldArray({
    control,
    name: 'sections',
    keyName: 'key',
  });

  async function updateInstruction(section: Section) {
    const sectionId = section.id;
    const instructions = section.instructions;

    return updateSection(
      { lessonId, bookId, chapterId, sectionId, subchapterId },
      { instructions },
    );
  }

  const { mutate: handleSubmit, isLoading } = useMutation(updateInstruction, {
    onSuccess: updated => {
      const newSections = sections.map(section =>
        section.id === updated.id ? updated : section,
      );

      const updatedSubchapterSections: Subchapter = {
        ...subchapter,
        sections: newSections,
      };

      const subchapters = chapter.items ?? [];

      const newSubchapters = subchapters.map(subchapter =>
        subchapter.id === subchapterId ? updatedSubchapterSections : subchapter,
      );

      onSubmit({
        ...chapter,
        items: newSubchapters,
      });

      alert.success(t('saved'));

      open();
    },
    onError: error => {
      alert.error(getErrorMessage(error));
    },
  });

  const getSection = useCallback(
    (sectionId: number) => {
      const section = sections.find(section => section.id === sectionId);
      const index = sections.findIndex(section => section.id === sectionId);

      return { section, index };
    },
    [sections],
  );

  const handleAdd = useCallback(
    (sectionId: number) => {
      const { section, index } = getSection(sectionId);

      if (!section || index === -1) return;

      const newSection = {
        ...section,
        instructions: t('newInstructions'),
        editing: true,
      };

      update(index, newSection);

      handleSubmit(newSection);
    },
    [getSection, handleSubmit, t, update],
  );

  const handleUpdate = useCallback(
    (sectionId: number, instructions: string | null) => {
      const { section, index } = getSection(sectionId);

      if (!section || index === -1) return;

      const newSection = {
        ...section,
        instructions,
      };

      update(index, newSection);

      handleSubmit(newSection);
    },
    [getSection, handleSubmit, update],
  );

  const handleRemove = useCallback(
    async (sectionId: number) => {
      const { section, index } = getSection(sectionId);

      if (!section || index === -1) return;

      const newSection = {
        ...section,
        instructions: null,
      };

      update(index, newSection);

      handleSubmit(newSection);
    },
    [getSection, handleSubmit, update],
  );

  return (
    <ModularCard className={{ base: 'w-72 max-h-[25.875rem]' }}>
      <ModularCard.Header
        icon={<TeacherBookColorIcon />}
        title={t('instructions')}
        isOpen={isOpen}
        onOpen={toggle}
        className={{ base: 'p-0 px-3.5 py-2.5' }}
        hideTooltip
      />
      <ModularCard.Accordion isOpen={isOpen}>
        <ModularCard.Division />
        <ul
          className={twJoin('flex flex-col gap-2 p-3.5', scrollBarYClassName)}
        >
          <ConditionalRenderer
            fallback={<Text text={t('noInstructions')} />}
            condition={sectionsWithInstructions.length > 0}
          >
            {fields.map(field => {
              if (!field.instructions) return null;
              return (
                <Instruction
                  key={field.id}
                  section={field}
                  readOnly={readOnly}
                  loading={isLoading}
                  handleUpdate={handleUpdate}
                  handleRemove={handleRemove}
                />
              );
            })}
          </ConditionalRenderer>
        </ul>
      </ModularCard.Accordion>
      <SectionsPopup
        sections={sectionsWithoutInstructions}
        loading={isLoading}
        readOnly={readOnly}
        handleAdd={handleAdd}
      />
    </ModularCard>
  );
}
